/**
 * PICKUP SYSTEM — the core Raiden loop.
 * Spec: bible 06-SHIPS-AND-WEAPONS §5.
 *
 * THE POD IS THE MECHANIC. A single object drifts down the screen alternating
 * red and blue. Red is HAILSTORM, a widening spread strongest close in. Blue is
 * ARCLIGHT, a narrow lance better at range and against armour. Grabbing the pod
 * on the colour you want means being in the right place at the right moment —
 * which is a live tactical decision every few seconds rather than an upgrade
 * ladder. Take it on the wrong colour and you have swapped weapons by mistake.
 *
 * That tension is the whole reason the genre feels the way it does, and it is
 * why the pod alternates rather than letting you simply pick.
 *
 * MEDALS are the chase object: pure score, no power, and the end-of-stage
 * bonus multiplies them by bombs held — so surviving without spending is worth
 * something concrete.
 */

import * as THREE from 'three'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'

export type PickupKind = 'pod' | 'medal' | 'bomb' | 'shield' | 'extend' | 'missile'

const MAX = 48

/** Seconds per red/blue flip while the pod drifts. */
const POD_FLIP_SECONDS = 0.85

export interface PickupCollect {
  kind: PickupKind
  /** For pods: the weapon state at the moment of contact. */
  weapon: 'HAILSTORM' | 'ARCLIGHT'
  x: number
  y: number
}

export class PickupSystem {
  readonly group = new THREE.Group()

  private readonly active = new Uint8Array(MAX)
  private readonly kind = new Uint8Array(MAX) // 0 pod, 1 medal, 2 bomb
  private readonly x = new Float32Array(MAX)
  private readonly y = new Float32Array(MAX)
  private readonly vx = new Float32Array(MAX)
  private readonly vy = new Float32Array(MAX)
  private readonly age = new Float32Array(MAX)

  private meshes: Partial<Record<PickupKind, THREE.InstancedMesh[]>> = {}
  private podRedMesh: THREE.InstancedMesh | null = null
  private podBlueMesh: THREE.InstancedMesh | null = null
  private readonly dummy = new THREE.Object3D()
  private time = 0

  constructor() {
    this.group.name = 'Pickups'
  }

  /** True when the pod is currently showing red (HAILSTORM). */
  get podIsRed(): boolean {
    return Math.floor(this.time / POD_FLIP_SECONDS) % 2 === 0
  }

  async load(): Promise<void> {
    const loader = new GLTFLoader()
    const specs: [PickupKind, string][] = [
      ['pod', 'pickup-pod'],
      ['medal', 'pickup-medal'],
      ['bomb', 'pickup-bomb'],
      ['shield', 'pickup-shield'],
      ['extend', 'pickup-extend'],
      ['missile', 'pickup-missile'],
    ]

    for (const [kind, id] of specs) {
      try {
        const gltf = await loader.loadAsync(`/models/thunderhead/${id}.glb`)
        const src = gltf.scene
        src.rotation.x = Math.PI / 2
        src.updateMatrixWorld(true)

        const list: THREE.InstancedMesh[] = []
        src.traverse((o) => {
          const m = o as THREE.Mesh
          if (!m.isMesh) return
          const g = m.geometry.clone()
          g.applyMatrix4(m.matrixWorld)
          const mat = Array.isArray(m.material) ? m.material[0] : m.material
          const inst = new THREE.InstancedMesh(g, mat, MAX)
          inst.name = `${id}_${m.name}`
          inst.frustumCulled = false
          inst.count = 0
          this.group.add(inst)
          list.push(inst)
          // Keep handles to the pod's two shells so they can alternate.
          if (kind === 'pod' && m.name === 'ShellRed') this.podRedMesh = inst
          if (kind === 'pod' && m.name === 'ShellBlue') this.podBlueMesh = inst
        })
        this.meshes[kind] = list
      } catch {
        /* a missing pickup model must not break the run */
      }
    }
  }

  spawn(kind: PickupKind, x: number, y: number): void {
    let i = -1
    for (let k = 0; k < MAX; k++) if (!this.active[k]) { i = k; break }
    if (i < 0) return
    this.active[i] = 1
    this.kind[i] = kind === 'pod' ? 0 : kind === 'medal' ? 1 : kind === 'bomb' ? 2 : kind === 'shield' ? 3 : kind === 'extend' ? 4 : 5
    this.x[i] = x
    this.y[i] = y
    this.age[i] = 0
    // Pods drift lazily and bounce off the walls; medals fall faster.
    this.vx[i] = kind === 'pod' ? (x > 0 ? -2.6 : 2.6) : 0
    this.vy[i] = kind === 'pod' ? -3.4 : kind === 'shield' || kind === 'extend' || kind === 'missile' ? -2.6 : -5.2
  }

  /**
   * Advance and resolve collection.
   * @param magnetRadius pull radius — medals drift toward the player so the
   *        chase never feels like pixel-hunting (bible 06 §5, magnet 3.5 u).
   */
  update(
    dt: number,
    playerX: number,
    playerY: number,
    contactRadius: number,
    magnetRadius: number,
  ): PickupCollect[] {
    this.time += dt
    const collected: PickupCollect[] = []

    for (let i = 0; i < MAX; i++) {
      if (!this.active[i]) continue
      this.age[i] += dt

      const dx = playerX - this.x[i]
      const dy = playerY - this.y[i]
      const d2 = dx * dx + dy * dy

      // Magnet: once inside the radius, accelerate toward the player.
      if (d2 < magnetRadius * magnetRadius) {
        const d = Math.sqrt(d2) || 1
        this.x[i] += (dx / d) * 14 * dt
        this.y[i] += (dy / d) * 14 * dt
      } else {
        this.x[i] += this.vx[i] * dt
        this.y[i] += this.vy[i] * dt
        // Pods bounce off the playfield walls rather than leaving — they are
        // too important to lose to a bad spawn position.
        if (this.kind[i] === 0 && Math.abs(this.x[i]) > 16.5) this.vx[i] *= -1
      }

      if (d2 < contactRadius * contactRadius) {
        collected.push({
          kind: (['pod', 'medal', 'bomb', 'shield', 'extend', 'missile'] as PickupKind[])[this.kind[i]],
          weapon: this.podIsRed ? 'HAILSTORM' : 'ARCLIGHT',
          x: this.x[i],
          y: this.y[i],
        })
        this.active[i] = 0
        continue
      }

      if (this.y[i] < -27) this.active[i] = 0
    }

    this.sync()
    return collected
  }

  private sync(): void {
    const counts: Record<number, number> = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
    const buckets: Record<number, number[]> = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [] }
    for (let i = 0; i < MAX; i++) {
      if (!this.active[i]) continue
      buckets[this.kind[i]].push(i)
      counts[this.kind[i]]++
    }

    const kinds: [PickupKind, number][] = [
      ['pod', 0], ['medal', 1], ['bomb', 2], ['shield', 3], ['extend', 4], ['missile', 5],
    ]
    for (const [kind, code] of kinds) {
      const list = this.meshes[kind]
      if (!list) continue
      for (const inst of list) {
        let n = 0
        for (const i of buckets[code]) {
          // Tumble so pickups catch light and read as objects, not decals.
          this.dummy.position.set(this.x[i], this.y[i], -1.2)
          this.dummy.rotation.set(0, 0, this.age[i] * 2.2)
          this.dummy.scale.setScalar(1)
          this.dummy.updateMatrix()
          inst.setMatrixAt(n++, this.dummy.matrix)
        }
        inst.count = n
        inst.instanceMatrix.needsUpdate = true
      }
    }

    // Alternate the pod's colour. This is the mechanic made visible.
    if (this.podRedMesh) this.podRedMesh.count = this.podIsRed ? counts[0] : 0
    if (this.podBlueMesh) this.podBlueMesh.count = this.podIsRed ? 0 : counts[0]
  }

  reset(): void {
    this.active.fill(0)
    this.time = 0
    this.sync()
  }

  dispose(): void {
    for (const list of Object.values(this.meshes)) {
      for (const inst of list ?? []) inst.geometry.dispose()
    }
  }
}
